import { useRef, useState } from 'react';
import ReactDatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './styles/date-picker-popper.css';

import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { FormFieldDatePickerHeader } from '@components/core-ui/form-elements/FormFieldDatePickerHeader';
import { FormFieldDatePickerHeaderMonth } from '@components/core-ui/form-elements/FormFieldDatePickerHeaderMonth';
import { FormFieldDatePickerRoot } from '@components/core-ui/form-elements/FormFieldDatePickerRoot';
import { FormFieldIcon } from '@components/core-ui/form-elements/FormFieldIcon';
import { FormFieldRoot } from '@components/core-ui/form-elements/FormFieldRoot';
import CalendarIcon from '@components/icons/Calendar.svg';
import ErrorIcon from '@components/icons/FormError.svg';
import LeftIcon from '@components/icons/ArrowLeft.svg';
import RightIcon from '@components/icons/ArrowRight.svg';
import {
  datePickerPopperClassName,
  datePickerPopperModifiers,
  datePickerPopperPlacement,
} from './helpers/datePickerPopperConfig';
import { useDatePickerOpenState } from './helpers/useDatePickerOpenState';
import { useDatePickerTypedInputCommit } from './helpers/useDatePickerTypedInputCommit';
import { months } from './helpers/months';
import { TProps } from './types/TProps';

export const DatePicker = ({
  label,
  onChange,
  onIconClick,
  value = null,
  minDate = new Date(0),
  maxDate = new Date(),
  required = false,
  disabled = false,
  error = false,
  showTimeSelect = false,
  className = 'form-input w-full',
  dateFormat = 'MM/dd/yyyy',
}: TProps) => {
  const defaultTitle = `${label}${required ? ' *' : ''}`;
  const [title, setTitle] = useState(defaultTitle);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const { isOpen, placement, handleCalendarOpen, handleCalendarClose } = useDatePickerOpenState(wrapperRef);
  const commitTypedInput = useDatePickerTypedInputCommit({
    wrapperRef,
    dateFormat,
    minDate,
    maxDate,
    selected: value,
    onCommit: onChange,
  });

  const onFocus = () => {
    setTitle('mm/dd/yyyy');
  };

  const onBlur = () => {
    commitTypedInput();
    setTitle(defaultTitle);
  };

  const renderIcon = () => {
    if (error) {
      if (!onIconClick) {
        return (
          <FormFieldIcon>
            <ErrorIcon />
          </FormFieldIcon>
        );
      }
      return (
        <FormFieldIcon clickable>
          <IconButtonClean type="button" onClick={onIconClick}>
            <ErrorIcon />
          </IconButtonClean>
        </FormFieldIcon>
      );
    }
    if (!onIconClick) {
      return (
        <FormFieldIcon>
          <CalendarIcon />
        </FormFieldIcon>
      );
    }
    return (
      <FormFieldIcon clickable>
        <IconButtonClean type="button" onClick={onIconClick}>
          <CalendarIcon />
        </IconButtonClean>
      </FormFieldIcon>
    );
  };

  return (
    <FormFieldRoot disabled={disabled} className={className}>
      <FormFieldDatePickerRoot ref={wrapperRef} data-open={isOpen} data-placement={placement ?? 'bottom'}>
        {renderIcon()}

        <ReactDatePicker
          dateFormat={dateFormat}
          placeholderText={title}
          showTimeSelect={showTimeSelect}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          popperClassName={datePickerPopperClassName}
          popperModifiers={datePickerPopperModifiers}
          popperPlacement={datePickerPopperPlacement}
          popperProps={{ strategy: 'fixed' }}
          onCalendarOpen={handleCalendarOpen}
          onCalendarClose={() => {
            commitTypedInput();
            handleCalendarClose();
          }}
          renderCustomHeader={({ date, decreaseMonth, increaseMonth }: ReactDatePickerCustomHeaderProps) => (
            <FormFieldDatePickerHeader>
              <IconButtonClean type="button" title="Previous month" onClick={decreaseMonth}>
                <LeftIcon />
              </IconButtonClean>
              <FormFieldDatePickerHeaderMonth>
                {months[date.getMonth()]} {date.getFullYear()}
              </FormFieldDatePickerHeaderMonth>
              <IconButtonClean type="button" title="Next month" onClick={increaseMonth}>
                <RightIcon />
              </IconButtonClean>
            </FormFieldDatePickerHeader>
          )}
          selected={value}
          onChange={(date) => onChange(date)}
          onSelect={(date) => onChange(date)}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </FormFieldDatePickerRoot>
    </FormFieldRoot>
  );
};
